import {defaultBoardLabels} from '@github-ui/board'
import type {BoardColumn, BoardRow} from '@github-ui/board-core'
import {useSuspenseQuery} from '@github-ui/react-query'
import {type ReactNode, Suspense, useMemo} from 'react'

import {BoardSurface} from './generic/BoardSurface'
import type {SavedViewsBoardAdapter, SavedViewsBoardItem, SavedViewsBoardOptions} from './types'

interface SavedViewBoardProps<TItem extends SavedViewsBoardItem, TMetadata> {
  adapter: SavedViewsBoardAdapter<TItem, TMetadata>
  options: SavedViewsBoardOptions
  renderCard: (item: TItem) => ReactNode
  renderColumnHeader?: (column: BoardColumn, metadata: TMetadata) => ReactNode
  renderRowHeader?: (row: BoardRow, metadata: TMetadata) => ReactNode
  /**
   * What the host says when the response filled no column. Optional, because only the host
   * knows whether that is a rejected query, a page past the end, a search that matched
   * nothing or no search at all, and its metadata is what carries the difference.
   */
  renderEmptyState?: (metadata: TMetadata) => ReactNode
}

export function SavedViewBoard<TItem extends SavedViewsBoardItem, TMetadata = undefined>(
  props: SavedViewBoardProps<TItem, TMetadata>,
) {
  return (
    <Suspense fallback={null}>
      <SavedViewBoardContent {...props} />
    </Suspense>
  )
}

function SavedViewBoardContent<TItem extends SavedViewsBoardItem, TMetadata>({
  adapter,
  options,
  renderCard,
  renderColumnHeader,
  renderRowHeader,
  renderEmptyState,
}: SavedViewBoardProps<TItem, TMetadata>) {
  const {data} = useSuspenseQuery({
    queryKey: ['saved-views-board', adapter.key, options],
    queryFn: ({signal}) => adapter.fetchInitialBoardPayload({...options, signal}),
    // Read only while the query holds nothing, so an adapter that answers it keeps the board
    // on the server and through hydration, and every read after that is the fetched payload.
    initialData: () => adapter.readInitialBoardPayload?.(options),
  })
  const renderHeader = useMemo(
    () => (renderColumnHeader ? (column: BoardColumn) => renderColumnHeader(column, data.metadata) : undefined),
    [renderColumnHeader, data],
  )
  const renderSwimlaneHeader = useMemo(
    () => (renderRowHeader ? (row: BoardRow) => renderRowHeader(row, data.metadata) : undefined),
    [renderRowHeader, data],
  )
  const renderEmpty = useMemo(
    () => (renderEmptyState ? () => renderEmptyState(data.metadata) : undefined),
    [renderEmptyState, data],
  )

  return (
    <BoardSurface
      boardData={data.boardData}
      renderCard={renderCard}
      renderColumnHeader={renderHeader}
      renderRowHeader={renderSwimlaneHeader}
      renderEmptyState={renderEmpty}
      labels={defaultBoardLabels}
    />
  )
}
